import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Users, TrendingUp, ExternalLink, MoreHorizontal, Edit, ChevronDown, ChevronUp, Download } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useUnifiedAuth } from '@/lib/auth/use-unified-auth';
import { supabase } from '@/lib/supabase/client';

interface ExperiencePerformance {
  experience_id: string;
  title: string;
  price: number;
  booking_count: number;
  revenue: number;
  net_revenue: number;
  participants: number;
  capacity_utilization: number;
  status: string;
}

interface RevenueAnalysis {
  gross_bookings: number;
  discounts: number;
  gross_revenue: number;
  payment_costs: number;
  net_revenue: number;
  net_rev_per_attendee: number;
}

export function CreatorExperienceTable() {
  const { user, profile } = useUnifiedAuth();
  const [experiences, setExperiences] = useState<ExperiencePerformance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedExperience, setExpandedExperience] = useState<string | null>(null);
  const [revenueAnalysis, setRevenueAnalysis] = useState<Record<string, RevenueAnalysis>>({});

  useEffect(() => {
    console.log('CreatorExperienceTable useEffect triggered, user:', user);
    if (user?.id) {
      fetchExperiences();
    }
  }, [user]);

  const fetchExperiences = async () => {
    try {
      console.log('fetchExperiences called, user:', user);
      if (!user?.id) {
        console.log('No user ID found for experiences, user:', user);
        // Don't set error here as user might still be loading
        return;
      }

      console.log('Starting to fetch creator profile for experiences, user ID:', user.id);

      // Get creator profile ID
      const { data: creatorProfile, error: profileError } = await supabase
        .from('creator_profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      console.log('Creator profile for experiences query result:', { creatorProfile, profileError });

      if (profileError || !creatorProfile) {
        console.log('Creator profile error or not found for experiences:', profileError);
        setError('Creator profile not found');
        setLoading(false);
        return;
      }

      console.log('Found creator profile for experiences:', creatorProfile);
      setLoading(true);

      // Get experiences with booking stats
      const { data: experiences, error: expError } = await supabase
        .from('experiences')
        .select('id, title, price, status')
        .eq('creator_profile_id', creatorProfile.id);

      if (expError) throw expError;

      // Get paid confirmed bookings for revenue calculations
      const { data: bookings, error: bookingError } = await supabase
        .from('bookings')
        .select('experience_id, total_amount, participant_count, created_at, discount_amount')
        .eq('payment_status', 'paid')
        .eq('status', 'confirmed')
        .in('experience_id', experiences?.map(e => e.id) || []);

      if (bookingError) throw bookingError;

      // Calculate experience performance
      const experiencePerformance = experiences?.map(exp => {
        const expBookings = bookings?.filter(b => b.experience_id === exp.id) || [];
        const revenue = expBookings.reduce((sum, b) => sum + (b.total_amount || 0), 0);
        const discounts = expBookings.reduce((sum, b) => sum + (b.discount_amount || 0), 0);
        const participants = expBookings.reduce((sum, b) => sum + (b.participant_count || 0), 0);
        const bookingCount = expBookings.length;
        
        // Calculate net revenue (after payment processing fees)
        const netRevenue = expBookings.reduce((sum, b) => {
          const grossAmount = b.total_amount || 0;
          const stripeFee = grossAmount * 0.045; // 4.5% estimate
          return sum + (grossAmount - stripeFee);
        }, 0);
        
        return {
          experience_id: exp.id,
          title: exp.title,
          price: exp.price,
          booking_count: bookingCount,
          revenue: revenue,
          net_revenue: netRevenue,
          participants: participants,
          capacity_utilization: 0, // Simplified
          status: exp.status
        };
      }) || [];

      // Calculate revenue analysis for each experience
      const analysisMap: Record<string, RevenueAnalysis> = {};
      experiencePerformance.forEach(exp => {
        const expBookings = bookings?.filter(b => b.experience_id === exp.experience_id) || [];
        const totalDiscounts = expBookings.reduce((sum, b) => sum + (b.discount_amount || 0), 0);
        const grossBookings = expBookings.reduce((sum, b) => sum + (b.total_amount || 0), 0);
        
        analysisMap[exp.experience_id] = {
          gross_bookings: grossBookings, // Sum of actual paid amounts
          discounts: totalDiscounts,
          gross_revenue: grossBookings - totalDiscounts, // Gross bookings less discounts
          payment_costs: 0, // Not using estimates
          net_revenue: exp.net_revenue,
          net_rev_per_attendee: exp.participants > 0 ? exp.net_revenue / exp.participants : 0
        };
      });

      setRevenueAnalysis(analysisMap);
      setExperiences(experiencePerformance);
    } catch (err) {
      console.error('Failed to fetch experiences:', err);
      setError(err instanceof Error ? err.message : 'Failed to load experiences');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-EU', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'text-green-400 bg-green-400/10';
      case 'pending': return 'text-yellow-400 bg-yellow-400/10';
      case 'draft': return 'text-sand-400 bg-sand-400/10';
      default: return 'text-sand-400 bg-sand-400/10';
    }
  };

  const toggleExpanded = (experienceId: string) => {
    setExpandedExperience(expandedExperience === experienceId ? null : experienceId);
  };

  const exportAttendeesCSV = async (experienceId: string, experienceTitle: string) => {
    try {
      // Get tickets for this experience
      const { data: tickets, error } = await supabase
        .from('tickets')
        .select(`
          id,
          ticket_number,
          guest_name,
          guest_email,
          checked_in,
          checked_in_at,
          bookings!inner(
            experience_id,
            user:users!bookings_user_id_fkey(full_name, email)
          )
        `)
        .eq('bookings.experience_id', experienceId)
        .order('ticket_number');

      if (error) throw error;

      // Format CSV data
      const csvHeaders = ['Ticket #', 'Name', 'Email', 'Checked In', 'Check-in Time'];
      const csvRows = tickets?.map(ticket => {
        const booking = ticket.bookings as any;
        return [
          ticket.ticket_number,
          ticket.guest_name || booking?.user?.full_name || 'N/A',
          ticket.guest_email || booking?.user?.email || 'N/A',
          ticket.checked_in ? 'Yes' : 'No',
          ticket.checked_in_at ? new Date(ticket.checked_in_at).toLocaleString() : ''
        ];
      }) || [];

      // Create CSV content
      const csvContent = [
        csvHeaders.join(','),
        ...csvRows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${experienceTitle.replace(/[^a-z0-9]/gi, '-')}-attendees-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export attendees:', err);
      alert('Failed to export attendee list');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="text-sand-400">Loading experiences...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
        <p className="text-sm text-red-500">{error}</p>
        <Button variant="outline" size="sm" onClick={fetchExperiences} className="mt-2">
          Retry
        </Button>
      </div>
    );
  }

  if (experiences.length === 0) {
    return (
      <div className="text-center py-12">
        <Calendar className="h-12 w-12 text-sand-400 mx-auto mb-4" />
        <p className="text-sand-400 mb-4">No experiences created yet</p>
        <Button size="sm" onClick={() => window.location.href = '/creator/experiences/new'}>
          Create Your First Experience
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-display text-sand-100">Experience Performance</h2>
        <Button size="sm" onClick={() => window.location.href = '/creator/experiences/new'}>
          New Experience
        </Button>
      </div>

      <div className="bg-earth-800/50 rounded-lg border border-earth-700/50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-earth-800/80">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-sand-300">Experience</th>
                <th className="text-left p-4 text-sm font-medium text-sand-300">Attendees</th>
                <th className="text-left p-4 text-sm font-medium text-sand-300">Bookings</th>
                <th className="text-left p-4 text-sm font-medium text-sand-300">Net Revenue</th>
                <th className="text-left p-4 text-sm font-medium text-sand-300">Per Attendee</th>
                <th className="text-left p-4 text-sm font-medium text-sand-300">Status</th>
                <th className="text-left p-4 text-sm font-medium text-sand-300">Actions</th>
              </tr>
            </thead>
            <tbody>
              {experiences.map((experience, index) => (
                <React.Fragment key={experience.experience_id}>
                  <motion.tr
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="border-t border-earth-700/50 hover:bg-earth-700/20 cursor-pointer"
                    onClick={() => toggleExpanded(experience.experience_id)}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        {expandedExperience === experience.experience_id ? (
                          <ChevronUp className="h-4 w-4 text-sand-400" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-sand-400" />
                        )}
                        <div>
                          <p className="text-sand-100 font-medium">{experience.title}</p>
                          <p className="text-sm text-sand-400">
                            {formatCurrency(experience.price)} per ticket
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-sand-400" />
                        <span className="text-sand-100">{experience.participants}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-sand-100">{experience.booking_count}</span>
                    </td>
                    <td className="p-4">
                      <span className="text-sand-100 font-mono">{formatCurrency(experience.net_revenue)}</span>
                    </td>
                    <td className="p-4">
                      <span className="text-sand-100 font-mono">
                        {formatCurrency(experience.participants > 0 ? experience.net_revenue / experience.participants : 0)}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(experience.status)}`}>
                        {experience.status}
                      </span>
                    </td>
                    <td className="p-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.location.href = `/experiences/${experience.experience_id}`}
                        >
                          <ExternalLink className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.location.href = `/creator/experiences/${experience.experience_id}/edit`}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => exportAttendeesCSV(experience.experience_id, experience.title)}
                        >
                          <Download className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  </motion.tr>
                  
                  {/* Revenue Analysis Dropdown */}
                  {expandedExperience === experience.experience_id && (
                    <tr>
                      <td colSpan={7} className="p-0">
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="bg-earth-900/30 p-6"
                        >
                          <h3 className="text-lg font-display text-sand-100 mb-4">Revenue Analysis</h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div className="bg-earth-800/50 p-4 rounded-lg">
                              <p className="text-sm text-sand-400 mb-1">Gross Bookings</p>
                              <p className="text-xl font-bold text-sand-100">
                                {formatCurrency(revenueAnalysis[experience.experience_id]?.gross_bookings || 0)}
                              </p>
                            </div>
                            <div className="bg-earth-800/50 p-4 rounded-lg">
                              <p className="text-sm text-sand-400 mb-1">Discounts</p>
                              <p className="text-xl font-bold text-sand-400">
                                {formatCurrency(revenueAnalysis[experience.experience_id]?.discounts || 0)}
                              </p>
                              <p className="text-xs text-sand-500">Applied at checkout</p>
                            </div>
                            <div className="bg-earth-800/50 p-4 rounded-lg">
                              <p className="text-sm text-sand-400 mb-1">Gross Revenue</p>
                              <p className="text-xl font-bold text-green-400">
                                {formatCurrency(revenueAnalysis[experience.experience_id]?.gross_revenue || 0)}
                              </p>
                              <p className="text-xs text-sand-500">
                                {formatCurrency((revenueAnalysis[experience.experience_id]?.gross_revenue || 0) / experience.participants)} per attendee
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => exportAttendeesCSV(experience.experience_id, experience.title)}
                            >
                              <Download className="h-4 w-4 mr-2" />
                              Export Attendee List
                            </Button>
                          </div>
                        </motion.div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary */}
      <div className="flex items-center justify-between text-sm text-sand-400 pt-4">
        <p>
          {experiences.length} experience{experiences.length !== 1 ? 's' : ''} •{' '}
          {experiences.reduce((sum, exp) => sum + exp.participants, 0)} total attendees •{' '}
          {formatCurrency(experiences.reduce((sum, exp) => sum + exp.net_revenue, 0))} net revenue
        </p>
      </div>
    </div>
  );
} 